import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';

import { invariant } from '@e-socna/utils';

import { useIsPricingEditable } from '../../contexts/PricingEditionContext/hooks/useIsPricingEditable.hook';
import { NoData } from '../Illustrations/NoData';

import { ServiceUsageCard } from './Card';
import { EmptyServiceUsages } from './EmptyServiceUsages';
import { NewServiceUsageCard } from './NewCard';
import { useGetServiceUsagesData } from './hooks/useGetServiceUsagesData.hook';
import { ServiceUsagesTotalCard } from './TotalCard';
import type { ListProps, ServiceUsageType } from './types/props.type';
import { InfoAlert } from '../InfoAlert';

export const ServiceUsages = <R extends boolean>({
  consultResolvableServiceInNewTab,
  editionMode,
  extraCards,
  fallbackServiceUsages,
  globalTotal,
  globalTotalErrorMessages,
  groupBanner,
  isInGroup = false,
  isInResolvableMode,
  manualTotal,
  maskAmounts = false,
  onStartCreate: handleStartCreate,
  onStartCustomizeAll: handleStartCustomizeAll,
  onStartCustomizeOne: handleStartCustomizeOne,
  onStartFlushAll: handleStartFlushAll,
  onStartFlushOne: handleStartFlushOne,
  onStartMove: handleStartMove,
  onStartRemove: handleStartRemove,
  onStartUpdate: handleStartUpdate,
  queryKeysToInvalidate,
  resolvableFieldSettings,
  serviceUsages,
  serviceUsagesGroupId,
  serviceUserId,
  showInheritanceAlert = true,
  showTotalCalculation = !isInGroup,
  switchTotalModeMutation,
  totalCalculation,
  totalModeResolvableFieldSettings,
  updateManualTotalMutation,
}: Readonly<ListProps<R>>) => {
  const isPricingEditable = useIsPricingEditable();

  invariant(
    !editionMode ||
      !!(handleStartCreate && handleStartRemove && handleStartUpdate),
    'onStartCreate, onStartRemove and onStartUpdate are required when Service usages are in edition mode',
  );

  const getServiceUsagesData = useGetServiceUsagesData();
  const serviceUsagesData = getServiceUsagesData<R>(
    isInResolvableMode,
    serviceUsages,
  );
  const fallbackServiceUsagesData = getServiceUsagesData<R>(
    isInResolvableMode,
    fallbackServiceUsages,
  );
  const serviceUsagesOrFallback =
    serviceUsagesData ?? fallbackServiceUsagesData;
  const areUsagesCatalogOrigin = !serviceUsagesData;

  const canInherit = !!fallbackServiceUsagesData;
  const mustInherit = canInherit && !serviceUsages;

  invariant(
    !editionMode ||
      !canInherit ||
      !!(
        handleStartCustomizeAll &&
        handleStartCustomizeOne &&
        handleStartFlushAll &&
        handleStartFlushOne
      ),
    'onStartCustomizeAll, onStartCustomizeOne, onStartFlushAll and onStartFlushOne are required when Service usages can be inherited in edition mode',
  );

  const handleStartCreateInEdition =
    editionMode && !mustInherit ? handleStartCreate : undefined;

  if (!editionMode && !isInGroup && !serviceUsagesOrFallback?.length) {
    return <NoData />;
  }

  return (
    <Box sx={{ pt: 2, width: '100%' }}>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!(showInheritanceAlert && canInherit) && (
        <InfoAlert
          action={
            <>
              {!!(
                editionMode &&
                handleStartCustomizeAll &&
                handleStartFlushAll
              ) && (
                <Button
                  color="inherit"
                  disabled={!isPricingEditable}
                  onClick={
                    mustInherit ? handleStartCustomizeAll : handleStartFlushAll
                  }
                  size="small"
                >
                  {mustInherit ? 'Spécifier' : 'Réinitialiser'}
                </Button>
              )}
            </>
          }
          key={String(mustInherit)}
          sx={{ mb: 2 }}
        >
          {mustInherit
            ? 'Le chiffrage est pour le moment hérité.'
            : 'Le chiffrage peut être hérité.'}
        </InfoAlert>
      )}
      {globalTotal}
      {groupBanner}
      {!!(showTotalCalculation && totalCalculation) && (
        <Box sx={{ mb: 2 }}>
          <ServiceUsagesTotalCard
            errorMessages={globalTotalErrorMessages}
            manualTotal={manualTotal}
            queryKeysToInvalidate={queryKeysToInvalidate}
            resolvableFieldSettings={totalModeResolvableFieldSettings}
            serviceUsagesGroupId={serviceUsagesGroupId}
            serviceUserId={serviceUserId}
            switchTotalModeMutation={
              mustInherit ? undefined : switchTotalModeMutation
            }
            title="Total du groupe"
            totalCalculation={totalCalculation}
            updateManualTotalMutation={
              mustInherit ? undefined : updateManualTotalMutation
            }
          />
        </Box>
      )}
      <Grid container spacing={2}>
        {!serviceUsagesOrFallback?.length ? (
          !(isInGroup && handleStartCreateInEdition) && (
            <EmptyServiceUsages isInGroup={isInGroup} />
          )
        ) : (
          <>
            {serviceUsagesOrFallback.map((serviceUsageOrFallback) => {
              const fallbackServiceUsage = (
                areUsagesCatalogOrigin
                  ? serviceUsageOrFallback
                  : fallbackServiceUsagesData?.find(
                      (fallbackServiceUsage) =>
                        fallbackServiceUsage._id ===
                        serviceUsageOrFallback.serviceUsageOrigin
                          ?.serviceUsageId,
                    )
              ) as ServiceUsageType<R> | undefined;

              return (
                <ServiceUsageCard
                  consultResolvableServiceInNewTab={
                    consultResolvableServiceInNewTab
                  }
                  editionMode={!!editionMode && !areUsagesCatalogOrigin}
                  fallbackServiceUsage={fallbackServiceUsage}
                  isInResolvableMode={isInResolvableMode}
                  key={serviceUsageOrFallback._id}
                  maskAmounts={maskAmounts}
                  onStartCustomizeOne={handleStartCustomizeOne}
                  onStartFlushOne={handleStartFlushOne}
                  onStartMove={handleStartMove}
                  onStartRemove={handleStartRemove}
                  onStartUpdate={handleStartUpdate}
                  resolvableFieldSettings={resolvableFieldSettings}
                  serviceUsage={
                    serviceUsageOrFallback as ServiceUsageType<R> | undefined
                  }
                />
              );
            })}
          </>
        )}
        {!!handleStartCreateInEdition && (
          <NewServiceUsageCard onStartCreate={handleStartCreateInEdition} />
        )}
        {!mustInherit && extraCards}
      </Grid>
    </Box>
  );
};
